<script setup lang="ts">
/**
 * Bar on top of an imported LINE history room (api/app/history.py, api/app/line_link.py): says what this read-only
 * chat is, whose it is (linked by the system / confirmed by whom) and leads back to the live chat.
 */
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { lineHistoryApi, openedFromLive, type LineHistoryView } from '../../api/linehistory'
import type { ConversationDetail } from '../../api/types'
import { barNote, spanText } from '../../lib/lineHistory'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ detail: ConversationDetail }>()
const router = useRouter()
const view = ref<LineHistoryView | null>(null)

watch(
  () => props.detail.id,
  async (id) => {
    view.value = null
    try {
      view.value = await lineHistoryApi().view(id)
    } catch {
      view.value = null // the bar still says what this chat is
    }
  },
  { immediate: true },
)

const hist = computed(() => (view.value?.kind === 'history' ? view.value : null))
const note = computed(() =>
  hist.value ? barNote(hist.value.link, spanText(hist.value.history.first_at, hist.value.history.last_at)) : 'ข้อความก่อนเริ่มใช้ระบบนี้ นำเข้าจากไฟล์ของ LINE OA',
)
const liveId = computed(() => hist.value?.link?.live_conversation_id ?? openedFromLive(props.detail.id))

function back(): void {
  if (liveId.value) void router.push({ name: 'conversation', params: { id: liveId.value } })
}
</script>

<template>
  <div class="lhb" role="note" aria-label="แชทเก่าจาก LINE">
    <div class="lhb-r">
      <Icon name="history" class="lhb-ic" />
      <div class="lhb-b">
        <b>แชทเก่าจาก LINE · อ่านอย่างเดียว</b>
        <span>{{ note }}</span>
      </div>
      <button v-if="liveId" type="button" class="lhb-back" @click="back"><Icon name="chevronLeft" /><span>กลับไปแชทปัจจุบัน</span></button>
    </div>
  </div>
</template>

<style scoped>
.lhb {
  flex: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 20px;
  border-bottom: 1px solid var(--line);
  background: #f7faf8;
}
.lhb-r {
  display: flex;
  align-items: center;
  gap: 10px;
}
.lhb-ic {
  flex: none;
  width: 18px;
  height: 18px;
  color: var(--ac2);
}
.lhb-b {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.lhb-b b {
  font-size: 13.5px;
  line-height: 21px;
  font-weight: 600;
  color: var(--ink);
}
.lhb-back {
  flex: none;
  height: 32px;
  padding: 0 12px 0 8px;
  border-radius: 9px;
  border: 1px solid #bfdccd;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ac2);
  white-space: nowrap;
}
.lhb-back:hover {
  background: var(--acs);
}
.lhb-back :deep(.ic) {
  width: 15px;
  height: 15px;
}
@container (max-width: 520px) {
  .lhb {
    padding: 10px 14px;
  }
  .lhb-r {
    flex-wrap: wrap;
  }
  .lhb-back {
    margin-left: 28px;
  }
}
</style>
